/**
 * Cookie 工具封装
 * 支持设置、获取、删除等操作
 * 支持自动序列化和反序列化
 */

export interface CookieOptions {
  expires?: Date | number; // 过期时间（天数或Date对象）默认100天
  path?: string; // 路径，默认 '/'
  domain?: string; // 域名
  secure?: boolean; // 是否只在 HTTPS 传输
  sameSite?: 'Strict' | 'Lax' | 'None'; // SameSite 属性
}

class Cookie {
  /**
   * 设置 Cookie
   * @param {string} name - Cookie 名称
   * @param {string | object} value - Cookie 值
   * @param {CookieOptions} options - 配置项
   */
  set(name: string, value: string | object, options: CookieOptions = { expires: 100 }): void {
    try {
      const {
        expires,
        path = '/',
        domain,
        secure = false,
        sameSite = 'Lax',
      } = options;

      // 序列化值
      const cookieValue = typeof value === 'string' ? value : JSON.stringify(value);
      const encodedValue = encodeURIComponent(cookieValue);

      // 构建 Cookie 字符串
      let cookieStr = `${encodeURIComponent(name)}=${encodedValue}`;

      if (expires) {
        let _expires: Date;
        if (typeof expires === 'number') {
          _expires = new Date();
          _expires.setTime(_expires.getTime() + expires * 24 * 60 * 60 * 1000);
        } else {
          _expires = expires;
        }
        cookieStr += `; Expires=${_expires.toUTCString()}`;
      }

      cookieStr += `; Path=${path}`;

      if (domain) {
        cookieStr += `; Domain=${domain}`;
      }

      if (secure) {
        cookieStr += '; Secure';
      }

      if (sameSite) {
        cookieStr += `; SameSite=${sameSite}`;
      }

      document.cookie = cookieStr;
    } catch (error) {
      console.error('设置 Cookie 失败:', error);
    }
  }

  /**
   * 获取 Cookie
   * @param {string} name - Cookie 名称
   * @param {T} defaultValue - 默认值（如果不存在）
   * @returns Cookie 值或默认值
   */
  get<T = string>(name: string, defaultValue?: T): T | undefined {
    const nameEQ = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(nameEQ)) {
        const value = decodeURIComponent(cookie.substring(nameEQ.length));
        try {
          // 尝试解析 JSON
          return JSON.parse(value) as T;
        } catch {
          // 返回原始字符串
          return value as unknown as T;
        }
      }
    }

    return defaultValue;
  }

  /**
   * 删除 Cookie
   * @param {string} name - Cookie 名称
   * @param {string} path - Cookie 路径（必须与设置时相同）
   * @param {string} domain - Cookie 域名（必须与设置时相同）
   */
  remove(name: string, options: CookieOptions = {}): void {
    this.set(name, '', {
      ...options,
      expires: new Date(0), // 设置为过期时间
    });
  }

  /**
   * 清空所有 Cookie
   * 仅清空当前路径和域名下的 Cookie
   */
  clear(options: CookieOptions = {}): void {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      this.remove(name, options);
    }
  }

  /**
   * 检查 Cookie 是否存在
   * @param {string} name - Cookie 名称
   */
  has(name: string): boolean {
    return this.get(name) !== undefined;
  }
}

// 导出单例
export const cookie = new Cookie();
